import React, { useState, useCallback, useRef, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import ProductCard from '../components/ProductCard';
import BarcodeScanner from '../components/BarcodeScanner';
import { addProductEvent, getProductEvents, removeProductEvent, savePendingProduct, saveScores, getProfile } from '../utils/storage';
import { getToday } from '../utils/dateUtils';
import { fetchByBarcode, searchProducts, searchBeautyProducts } from '../services/productApi';
import { computeDailyOverview } from '../scoring/edcScoring';
import { logMissingProduct } from '../services/loggingService';
import { Camera, Search, AlertTriangle, X, Send } from 'lucide-react';

export default function ProductScan() {
    const today = getToday();
    const [searchParams] = useSearchParams();
    const avoidParam = searchParams.get('avoid') || '';
    const initialQuery = searchParams.get('q') || '';

    const [search, setSearch] = useState(initialQuery);
    const [expandedProduct, setExpandedProduct] = useState(null);
    const [todayProducts, setTodayProducts] = useState(getProductEvents(today));

    // Barcode lookup state
    const [barcode, setBarcode] = useState('');
    const [barcodeLoading, setBarcodeLoading] = useState(false);
    const [barcodeResult, setBarcodeResult] = useState(null);
    const [showSubmitForm, setShowSubmitForm] = useState(false);
    const [showScanner, setShowScanner] = useState(false);

    // API search state
    const [apiResults, setApiResults] = useState([]);
    const [apiLoading, setApiLoading] = useState(false);
    const [apiError, setApiError] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [hasMore, setHasMore] = useState(false);
    const searchTimerRef = useRef(null);

    const [submitForm, setSubmitForm] = useState({ name: '', brand: '', ingredients: '', category: 'personal-care' });

    useEffect(() => {
        if (initialQuery && apiResults.length === 0) {
            handleSearchChange(initialQuery);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initialQuery]);

    const handleBarcodeLookup = useCallback(async (code) => {
        const target = (code || barcode).trim();
        if (!target) return;
        setBarcodeLoading(true);
        setBarcodeResult(null);
        setShowSubmitForm(false);
        setSearch('');
        setApiResults([]);

        const result = await fetchByBarcode(target);
        setBarcodeResult(result);
        setBarcodeLoading(false);

        if (!result.product && !result.error) {
            setShowSubmitForm(true);
        }
    }, [barcode]);

    const handleSearchChange = useCallback((value) => {
        setSearch(value);
        setApiError(null);
        setBarcodeResult(null);
        setShowSubmitForm(false);
        setCurrentPage(1);
        setHasMore(false);

        if (searchTimerRef.current) clearTimeout(searchTimerRef.current);

        if (value.trim().length >= 3) {
            searchTimerRef.current = setTimeout(async () => {
                setApiLoading(true);
                // Run in parallel — proxy handles CORS, and OBF failure won't block OFF results
                const [foodRes, beautyRes] = await Promise.all([
                    searchProducts(value, 1, 10),
                    searchBeautyProducts(value, 1, 10),
                ]);
                setApiLoading(false);
                const combined = [...(foodRes.products || []), ...(beautyRes.products || [])];
                setApiResults(combined);
                
                const total = (foodRes.total || 0) + (beautyRes.total || 0);
                setHasMore(total > combined.length);

                // Only show error UI if we got zero results AND there was a hard failure
                if (combined.length === 0) {
                    if (foodRes.error) setApiError(foodRes.error);
                    else if (beautyRes.error) setApiError(beautyRes.error);
                } else {
                    if (foodRes.error) console.warn('OFF partial failure:', foodRes.error);
                    if (beautyRes.error) console.warn('OBF partial failure:', beautyRes.error);
                }
            }, 800);
        } else {
            setApiResults([]);
        }
    }, []);
    const handleLoadMore = async () => {
        if (apiLoading || !hasMore) return;
        
        const nextPage = currentPage + 1;
        setApiLoading(true);
        
        try {
            // Sequential to reduce server load (same as initial search)
            const foodRes = await searchProducts(search, nextPage, 10);
            const beautyRes = await searchBeautyProducts(search, nextPage, 10);
            
            const nextResults = [...(foodRes.products || []), ...(beautyRes.products || [])];
            setApiResults(prev => [...prev, ...nextResults]);
            setCurrentPage(nextPage);
            
            // No more results if page came back empty
            if (nextResults.length === 0) {
                setHasMore(false);
            }
        } catch (e) {
            setApiError('Failed to load more results.');
        } finally {
            setApiLoading(false);
        }
    };


    const handleAdd = (product) => {
        const adapted = {
            ...product,
            ingredients: product.ingredients || product.ingredients_text || '',
            name: product.name || product.product_name || 'Unknown',
        };
        // frequency is now passed by ProductCard
        const freq = product.frequency || 'daily';
        addProductEvent(today, { product: adapted, frequency: freq, addedAt: new Date().toISOString() });
        const updatedEvents = getProductEvents(today);
        setTodayProducts(updatedEvents);
        // Auto-save score so History page has data
        const overview = computeDailyOverview(updatedEvents, getProfile() || {});
        saveScores(today, { ...overview, timestamp: new Date().toISOString() });
        setBarcodeResult(null);
        setSearch('');
        setApiResults([]);
        setBarcode('');
        setExpandedProduct(null);
    };

    const handleRemove = (index) => {
        removeProductEvent(today, index);
        const updatedEvents = getProductEvents(today);
        setTodayProducts(updatedEvents);
        // Re-save score after removal
        const overview = computeDailyOverview(updatedEvents, getProfile() || {});
        saveScores(today, { ...overview, timestamp: new Date().toISOString() });
    };

    const handleSubmit = () => {
        if (!submitForm.name.trim()) return;
        const productData = {
            barcode: barcode.trim(),
            name: submitForm.name.trim(),
            brand: submitForm.brand.trim(),
            ingredients: submitForm.ingredients.trim(),
            category: submitForm.category,
            image_url: null,
        };
        const product = savePendingProduct(productData);
        
        // Log to external service
        logMissingProduct(productData);

        setBarcodeResult({ product, source: 'user', fromCache: false, error: null });
        setShowSubmitForm(false);
    };

    const handleScan = (code) => {
        setShowScanner(false);
        setBarcode(code);
        handleBarcodeLookup(code);
    };

    const clearAll = () => {
        setBarcodeResult(null);
        setShowSubmitForm(false);
        setSearch('');
        setApiResults([]);
        setBarcode('');
    };

    const isShowingResults = barcodeResult || showSubmitForm || apiResults.length > 0 || barcodeLoading || apiLoading;

    return (
        <div className="scan-page">
            {showScanner && (
                <BarcodeScanner onScan={handleScan} onClose={() => setShowScanner(false)} />
            )}

            {!isShowingResults && (
                <div className="scan-hero">
                    <h1 className="scan-title">Check a Product</h1>
                    <p className="scan-subtitle">Search or scan to see exposure signals</p>

                    <button className="scan-camera-btn" onClick={() => setShowScanner(true)}>
                        <Camera size={32} />
                        <span>Scan Barcode</span>
                    </button>
                </div>
            )}

            <div className="scan-search-bar">
                <div className="scan-search-inner">
                    <Search size={16} className="scan-search-icon" />
                    <input
                        className="scan-search-input"
                        type="text"
                        inputMode="search"
                        placeholder="Search name, brand, or barcode..."
                        value={search || barcode}
                        onChange={(e) => {
                            const v = e.target.value;
                            if (/^\d+$/.test(v.trim()) && v.trim().length > 6) {
                                setBarcode(v);
                                setSearch('');
                            } else {
                                setSearch(v);
                                setBarcode('');
                            }
                            handleSearchChange(v);
                        }}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' && barcode.trim()) handleBarcodeLookup();
                        }}
                    />
                    {(search || barcode) && (
                        <button className="scan-clear-btn" onClick={clearAll}>
                            <X size={14} />
                        </button>
                    )}
                    <button className="scan-camera-pill" onClick={() => setShowScanner(true)} title="Scan barcode">
                        <Camera size={16} />
                    </button>
                </div>
            </div>

            <div className="scan-results">
                {(barcodeLoading || apiLoading) && (
                    <div className="scan-loading">
                        <div className="spinner" />
                        <span>Checking signals…</span>
                    </div>
                )}

                {apiError && (
                    <div className="error-message">
                        <AlertTriangle size={14} style={{ marginRight: 4, verticalAlign: -2 }} />
                        {apiError}
                    </div>
                )}

                {barcodeResult?.error && (
                    <div className="error-message">
                        <AlertTriangle size={14} style={{ marginRight: 4, verticalAlign: -2 }} />
                        {barcodeResult.error}
                    </div>
                )}

                {barcodeResult?.product && (
                    <div className="fade-in">
                        <p className="scan-section-label">Found item</p>
                        <ProductCard
                            product={barcodeResult.product}
                            onAdd={handleAdd}
                            expanded={true}
                            showDetails={() => { }}
                        />
                    </div>
                )}

                {showSubmitForm && (
                    <div className="fade-in">
                        <div className="not-found-card">
                            <div className="not-found-icon"><Search size={28} /></div>
                            <div className="not-found-title">Not in database</div>
                            <div className="not-found-sub">Add it manually to check ingredients.</div>
                            <div className="submit-form">
                                <input className="text-input" type="text" placeholder="Product name *"
                                    value={submitForm.name} onChange={e => setSubmitForm(f => ({ ...f, name: e.target.value }))} />
                                <input className="text-input" type="text" placeholder="Brand"
                                    value={submitForm.brand} onChange={e => setSubmitForm(f => ({ ...f, brand: e.target.value }))} style={{ marginTop: 8 }} />
                                <select className="text-input" value={submitForm.category}
                                    onChange={e => setSubmitForm(f => ({ ...f, category: e.target.value }))} style={{ marginTop: 8 }}>
                                    <option value="personal-care">Personal Care</option>
                                    <option value="food">Food</option>
                                    <option value="household">Household</option>
                                    <option value="baby-care">Baby Care</option>
                                    <option value="food-packaging">Food Packaging</option>
                                </select>
                                <textarea className="text-input" rows={3} placeholder="Ingredients list (optional)"
                                    value={submitForm.ingredients} onChange={e => setSubmitForm(f => ({ ...f, ingredients: e.target.value }))} style={{ marginTop: 8 }} />
                                <button className="btn btn-primary btn-block" onClick={handleSubmit}
                                    disabled={!submitForm.name.trim()} style={{ marginTop: 12, opacity: submitForm.name.trim() ? 1 : 0.4 }}>
                                    Check Product
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {apiResults.length > 0 && (
                    <div className="fade-in">
                        <p className="scan-section-label">Search results</p>
                        {apiResults.map((product, i) => (
                            <ProductCard
                                key={product.id || i}
                                product={product}
                                onAdd={handleAdd}
                                expanded={expandedProduct === (product.id || i)}
                                showDetails={() => setExpandedProduct(expandedProduct === (product.id || i) ? null : (product.id || i))}
                                avoidSignal={avoidParam}
                            />
                        ))}
                        {hasMore && (
                            <button 
                                className="btn-load-more" 
                                onClick={handleLoadMore} 
                                disabled={apiLoading}
                                style={{ marginTop: 16, width: '100%' }}
                            >
                                {apiLoading ? 'Loading...' : 'Load more results'}
                            </button>
                        )}
                    </div>
                )}
                {apiResults.length === 0 && search.trim().length >= 3 && !apiLoading && !barcodeResult && !showSubmitForm && (
                    <div className="fade-in">
                        <div className="not-found-mini">
                            <p>No matches found for "{search}"</p>
                            <button className="btn-text" onClick={() => logMissingProduct({ search_query: search, type: 'EMPTY_SEARCH' })}>
                                <Send size={12} style={{ marginRight: 4 }} /> Report missing item
                            </button>
                        </div>
                    </div>
                )}
                {todayProducts.length > 0 && !isShowingResults && (
                    <div className="fade-in">
                        <p className="scan-section-label" style={{ marginTop: 32 }}>Recent checks</p>
                        {todayProducts.map((event, i) => (
                            <ProductCard
                                key={i}
                                product={event.product}
                                expanded={expandedProduct === `recent-${i}`}
                                showDetails={() => setExpandedProduct(expandedProduct === `recent-${i}` ? null : `recent-${i}`)}
                                onRemove={() => handleRemove(i)}
                            />
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}
